import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '../api/client.js';
import type { DashboardResponse, WorkOrderListResponse } from '../api/types.js';
import { useSession } from '../auth/SessionProvider.js';
import { PERMISSIONS } from '../../shared/permissions.js';
import { WORK_ORDER_STATUS_LABELS } from '../../shared/workflow.js';
import type { WorkOrderStatus } from '../../shared/workflow.js';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  PageHeader,
  PriorityBadge,
  Spinner,
  StatusBadge,
} from '../components/ui.js';
import { formatCurrency, formatDate, formatRelative } from '../lib/format.js';

/** The statuses shown as a pipeline, in workflow order. */
const PIPELINE: WorkOrderStatus[] = [
  'submitted',
  'under_vgs_review',
  'estimate_in_progress',
  'awaiting_customer_approval',
  'approved',
  'procurement',
  'repair_scheduled',
  'work_in_progress',
  'completed',
];

export function DashboardPage() {
  const { user, can, isCustomer } = useSession();

  const {
    data: dashboard,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => apiRequest<DashboardResponse>('/api/dashboard'),
  });

  const { data: recent } = useQuery({
    queryKey: ['work-orders', 'recent'],
    queryFn: () =>
      apiRequest<WorkOrderListResponse>('/api/work-orders', {
        query: { limit: 8, sort: 'updated_at', direction: 'desc' },
      }),
  });

  if (isLoading) return <Spinner label="Loading your dashboard" />;
  if (error) {
    return (
      <Alert variant="error">Your dashboard could not be loaded. Please refresh the page.</Alert>
    );
  }

  const showFinancials = can(PERMISSIONS.REPORT_FINANCIAL_VIEW);

  return (
    <>
      <PageHeader
        title={`Welcome, ${user?.fullName.split(' ')[0] ?? ''}`}
        subtitle={
          isCustomer
            ? 'Follow your repair requests and review proposals awaiting your decision.'
            : 'Work assigned to you and requests awaiting VGS action.'
        }
        actions={
          can(PERMISSIONS.WORK_ORDER_CREATE) && (
            <Link to="/work-orders/new">
              <Button>New work order</Button>
            </Link>
          )
        }
      />

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile
          label="Open work orders"
          value={dashboard?.openCount ?? 0}
          to="/work-orders?view=open"
        />
        <StatTile
          label={isCustomer ? 'Awaiting your approval' : 'Awaiting customer approval'}
          value={dashboard?.awaitingApprovalCount ?? 0}
          to={isCustomer ? '/approvals' : '/work-orders?status=awaiting_customer_approval'}
          emphasis={(dashboard?.awaitingApprovalCount ?? 0) > 0}
        />
        <StatTile
          label="Past requested date"
          value={dashboard?.overdueCount ?? 0}
          to="/work-orders?view=overdue"
        />
        <StatTile
          label="Closed in last 30 days"
          value={dashboard?.closedLast30Days ?? 0}
          to="/work-orders?status=closed"
        />
      </div>

      {showFinancials && (
        <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Card title="Approved value (all approved work orders)">
            <p className="text-2xl font-semibold text-slate-900">
              {formatCurrency(dashboard?.approvedValueCents ?? 0)}
            </p>
            <p className="mt-1 text-xs text-slate-600">
              {dashboard?.approvedCount ?? 0} work order{dashboard?.approvedCount === 1 ? '' : 's'}{' '}
              USPS has approved, whatever stage they are at now. The Pipeline&apos;s
              &quot;Approved&quot; row counts only those not yet scheduled.
            </p>
          </Card>
          <Card title="Pending proposal value">
            <p className="text-2xl font-semibold text-slate-900">
              {formatCurrency(dashboard?.pendingValueCents ?? 0)}
            </p>
            <p className="mt-1 text-xs text-slate-600">Submitted proposals not yet decided.</p>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Card title="Pipeline" className="lg:col-span-1">
          <ul className="flex flex-col gap-1.5">
            {PIPELINE.map((status) => {
              const count = dashboard?.byStatus[status] ?? 0;
              return (
                <li key={status}>
                  <Link
                    to={`/work-orders?status=${status}`}
                    className="flex items-center justify-between gap-3 rounded px-2 py-1.5 hover:bg-slate-50"
                  >
                    <span className="text-sm text-slate-700">
                      {WORK_ORDER_STATUS_LABELS[status]}
                    </span>
                    <span className="text-sm font-semibold tabular-nums text-slate-900">
                      {count}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </Card>

        <Card title="Recent activity" className="lg:col-span-2" bodyClassName="px-0 py-0 sm:px-0">
          {!recent || recent.items.length === 0 ? (
            <EmptyState
              title="No work orders yet"
              description={
                can(PERMISSIONS.WORK_ORDER_CREATE)
                  ? 'Enter a repair request to get started.'
                  : 'Work orders will appear here once VGS enters them.'
              }
            />
          ) : (
            <ul className="divide-y divide-slate-200">
              {recent.items.map((item) => (
                <li key={item.id}>
                  <Link
                    to={`/work-orders/${item.id}`}
                    className="flex flex-col gap-1.5 px-4 py-3 hover:bg-slate-50 sm:px-5"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs font-semibold text-brand-800">
                        {item.workOrderNumber}
                      </span>
                      <StatusBadge status={item.status} kind={item.kind} />
                      <PriorityBadge priority={item.priority} />
                    </div>
                    <p className="text-sm font-medium text-slate-900">{item.title}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
                      <span>
                        {item.facilityCode} · {item.facilityName}
                      </span>
                      <span>Submitted {formatDate(item.submittedAt)}</span>
                      <span>Updated {formatRelative(item.updatedAt)}</span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}

function StatTile({
  label,
  value,
  to,
  emphasis = false,
}: {
  label: string;
  value: number;
  to: string;
  emphasis?: boolean;
}) {
  return (
    <Link
      to={to}
      className={[
        'rounded-lg bg-white p-4 shadow-sm ring-1 transition hover:shadow',
        emphasis && value > 0 ? 'ring-amber-300' : 'ring-slate-200',
      ].join(' ')}
    >
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1.5 text-2xl font-semibold tabular-nums text-slate-900">{value}</p>
    </Link>
  );
}
